import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useHasVerticalOverflow } from '@avanta/shared/hooks/useHasVerticalOverflow';
import { type Psychologist } from '@avanta/shared/types/psychologists';
import { cn } from '@avanta/shared/utils/cn';
import { ChevronRight } from 'lucide-react';
import { useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AppointmentSteps } from 'types/appointments';
import { PSYCHOLOGISTS_KEY_PREFIX } from '..';
import { AppointmentsReservePayload, type StepProps } from '../../../../../types';
import { BackButton } from '../../../../ui/BackButton';
import { PsychoCheckbox } from '../../../../ui/PsychoCheckbox';
import { SessionSummary } from '../../../../ui/SessionSummary';
import { StepSection } from '../../../../ui/StepSection';
import { useFetchTopPsychologists } from '../../useFetchTopPsychologists';
import { PsychologistStepSkeleton } from '../PsychologistStepSkeleton';

export const DesktopView = ({
  handleNext,
  handleBack,
  nextStepName,
  handleOnCheckedChange,
}: StepProps & {
  handleOnCheckedChange: (checked: boolean, psychologist: Psychologist) => void;
}): React.ReactElement => {
  const { watch } = useFormContext();
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const {
    psychologists: filteredPsychologists,
    isLoading,
    isFetching,
  } = useFetchTopPsychologists();
  const isLoadingPsychologists = isLoading || isFetching;

  const psychologist = watch(AppointmentsReservePayload.psychologist);
  const nextStepLabel =
    nextStepName === AppointmentSteps.payment ? 'continueWithPayment' : 'continueWithConfirmation';

  const layoutRootRef = useRef<HTMLDivElement>(null);
  const { hasOverflow } = useHasVerticalOverflow(layoutRootRef, 'element', filteredPsychologists);

  return (
    <div
      className={cn('hidden am-lg:flex flex-col gap-10 min-h-0 relative', hasOverflow && 'gap-2')}
    >
      <div className="flex gap-6 w-full flex-1 min-h-0">
        <SessionSummary title={t(`${PSYCHOLOGISTS_KEY_PREFIX}yourSelection.title`)} showLanguages />
        <StepSection
          title={t(`${PSYCHOLOGISTS_KEY_PREFIX}availableProfessionals.title`)}
          className="w-full flex flex-col min-h-0"
          containerClassName="am-lg:gap-2"
          fullWidth
        >
          <div
            ref={layoutRootRef}
            className={cn('flex flex-col gap-4 overflow-y-auto pr-1 pb-3 min-h-0 scrollbar-custom')}
          >
            {isLoadingPsychologists ? (
              <PsychologistStepSkeleton />
            ) : (
              filteredPsychologists?.map(
                ({ psychologist: item }: { psychologist: Psychologist }) => (
                  <PsychoCheckbox
                    key={item.id}
                    psychologist={item}
                    checked={item.id === psychologist?.id}
                    onCheckedChange={(checked: boolean) => handleOnCheckedChange(checked, item)}
                  />
                )
              )
            )}
          </div>
        </StepSection>
      </div>
      <div className={cn('flex justify-between gap-4 pt-2')}>
        <BackButton handleBack={handleBack} />
        <Button
          type="button"
          variant="primary"
          className="max-am-2xl:w-fit am-2xl:max-w-138"
          icon="trailing"
          size="lg"
          onClick={handleNext}
          disabled={!psychologist}
        >
          {t(`AppointmentsReserve.Steps.${nextStepLabel}`)}
          <ChevronRight className="size-5" />
        </Button>
      </div>
    </div>
  );
};
